import { useState } from 'react';
import { downloadCsv, timestampedFilename, toCsv, type CsvColumn } from '@/utils/csv';
import { Search, Info, UserPlus, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { YourCheckaNavigation } from '@/components/Navbar';
import CreateStaffModal from '@/components/modals/CreateStaffModal';
import UserDetailsModal from '@/components/modals/UserDetailsModal';
import { useGetAllUsersQuery } from '@/store/api/apiSlice';
import { User, UserRole, UserStatus } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

type FilterType =
    | 'All'
    | 'Employers'
    | 'Agents'
    | 'HR Partners'
    | 'Super Admins'
    | 'Customer Success'
    | 'Medical'
    | 'Notary'
    | 'Police';

const roleMap: Record<UserRole, FilterType> = {
    [UserRole.EMPLOYER]: 'Employers',
    [UserRole.AGENT]: 'Agents',
    [UserRole.HR]: 'HR Partners',
    [UserRole.SUPER_ADMIN]: 'Super Admins',
    [UserRole.CUSTOMER_SUCCESS]: 'Customer Success',
    [UserRole.MEDICAL]: 'Medical',
    [UserRole.NOTARY]: 'Notary',
    [UserRole.POLICE]: 'Police',
};

export default function Users() {
    const currentUser = useAppSelector(selectCurrentUser);
    const [filter, setFilter] = useState<FilterType>('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // Keyed by user id, not row index. DataTable addresses rows by their
    // position on the *current page*, so storing indices meant a selection made
    // on page 1 re-appeared against whichever rows happened to sit in those
    // positions on page 2 — and would have exported the wrong people.
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);

    // Fetch users from API
    const { data: users = [], isLoading } = useGetAllUsersQuery({});

    // Get user initials
    const getInitials = (firstName?: string, lastName?: string) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return (first + last).toUpperCase() || 'U';
    };

    const filteredData = users.filter((item) => {
        const itemRole = roleMap[item.role];
        const matchesFilter = filter === 'All' || itemRole === filter;
        const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
        const email = item.email.toLowerCase();
        const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || email.includes(searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    // DataTable hands back a page-local index; translate it to a stable id.
    const handleRowSelect = (index: number) => {
        const user = paginatedData[index];
        if (!user) return;

        const next = new Set(selectedIds);
        if (next.has(user.id)) next.delete(user.id);
        else next.add(user.id);
        setSelectedIds(next);
    };

    // Back to page-local indices for DataTable's own rendering.
    const selectedRows = new Set(
        paginatedData.map((user, index) => (selectedIds.has(user.id) ? index : -1)).filter((index) => index !== -1),
    );

    // Selection wins when present; otherwise export whatever the role chips and
    // search box currently show — never the unfiltered table behind them.
    const exportRows = selectedIds.size > 0 ? filteredData.filter((user) => selectedIds.has(user.id)) : filteredData;

    const handleExportCsv = () => {
        const columnsForCsv: CsvColumn<User>[] = [
            { header: 'S/N', value: (_user, index) => index + 1 },
            { header: 'Full Name', value: (user) => `${user.firstName} ${user.lastName}`.trim() },
            { header: 'Email', value: (user) => user.email },
            { header: 'Phone Number', value: (user) => user.phoneNumber || '' },
            { header: 'Role', value: (user) => formatRole(user.role) },
            { header: 'Status', value: (user) => user.status },
            { header: 'Date Joined', value: (user) => new Date(user.createdAt).toLocaleDateString() },
            // Not a visible column, but the reason a row shows the Complimentary
            // badge — dropping it would make the export disagree with the table.
            { header: 'Complimentary', value: (user) => (user.paymentExempt ? 'Yes' : 'No') },
        ];

        downloadCsv(timestampedFilename('yourchecka-users'), toCsv(exportRows, columnsForCsv));
    };

    const getStatusColor = (status: UserStatus) => {
        switch (status) {
            case UserStatus.ACTIVE:
                return 'bg-green-50 text-green-700 border-green-100';
            case UserStatus.INACTIVE:
                return 'bg-gray-50 text-gray-700 border-gray-100';
            case UserStatus.SUSPENDED:
                return 'bg-red-50 text-red-700 border-red-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const getRoleColor = (role: UserRole) => {
        switch (role) {
            case UserRole.EMPLOYER:
                return 'bg-blue-50 text-blue-700 border-blue-100';
            case UserRole.AGENT:
                return 'bg-purple-50 text-purple-700 border-purple-100';
            case UserRole.HR:
                return 'bg-orange-50 text-orange-700 border-orange-100';
            case UserRole.SUPER_ADMIN:
                return 'bg-amber-50 text-amber-700 border-amber-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const formatRole = (role: UserRole) => {
        switch (role) {
            case UserRole.EMPLOYER:
                return 'Employer';
            case UserRole.AGENT:
                return 'Agent';
            case UserRole.HR:
                return 'HR Partner';
            case UserRole.SUPER_ADMIN:
                return 'Super Admin';
            default:
                return role;
        }
    };

    const columns: Column<User>[] = [
        {
            key: 'id',
            label: 'S/N',
            sortable: true,
            render: (_item, index) => (
                <span className="text-sm text-gray-700">{(currentPage - 1) * rowsPerPage + index + 1}</span>
            ),
        },
        {
            key: 'firstName',
            label: 'Full Name',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <div>
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        {item.firstName} {item.lastName}
                        {item.role === UserRole.EMPLOYER && item.paymentExempt && (
                            <span className="inline-flex items-center rounded-full bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                                Complimentary
                            </span>
                        )}
                    </p>
                    <p className="text-xs text-gray-500">{item.email}</p>
                </div>
            ),
        },
        {
            key: 'phoneNumber',
            label: 'Phone Number',
            sortable: true,
            hasMenu: true,
            render: (item) => <span className="text-sm text-gray-700">{item.phoneNumber || '---'}</span>,
        },
        {
            key: 'role',
            label: 'Role',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <Badge variant="outline" className={`${getRoleColor(item.role)} px-4 py-1 rounded-lg font-medium`}>
                    {formatRole(item.role)}
                </Badge>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <Badge
                    variant="outline"
                    className={`${getStatusColor(item.status)} px-4 py-1 rounded-lg font-medium capitalize`}
                >
                    {item.status.toLowerCase()}
                </Badge>
            ),
        },
        {
            key: 'createdAt',
            label: 'Date Joined',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <span className="text-sm text-gray-700">{new Date(item.createdAt).toLocaleDateString()}</span>
            ),
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (item) => (
                <Button
                    onClick={() => {
                        setSelectedUser(item);
                        setShowUserModal(true);
                    }}
                    className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-xl text-sm font-medium"
                >
                    View Details
                </Button>
            ),
        },
    ];

    if (isLoading) {
        return (
            <main className="w-full h-screen flex items-center justify-center px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading users...</p>
                </div>
            </main>
        );
    }

    return (
        <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                {/* Header/Navbar */}
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={3}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
                    only={true}
                    className="border-b border-gray-100"
                />

                {/* Content */}
                <div className="flex-1 overflow-hidden p-8 md:p-10 flex flex-col">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Users</h1>
                        <div className="flex items-start gap-2 text-gray-600">
                            <Info className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <p className="text-sm leading-relaxed">
                                Manage all users in the system. You can view user details, update their status, and
                                monitor their activities.
                            </p>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                        {/* Search */}
                        <div className="relative max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or email"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={() => setFilter('All')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'All'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilter('Employers')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Employers'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Employers
                            </button>
                            <button
                                onClick={() => setFilter('Agents')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Agents'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Agents
                            </button>
                            <button
                                onClick={() => setFilter('HR Partners')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'HR Partners'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                HR Partners
                            </button>
                            <button
                                onClick={() => setFilter('Super Admins')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Super Admins'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Super Admins
                            </button>
                            <button
                                onClick={() => setFilter('Customer Success')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Customer Success'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Customer Success
                            </button>
                            <button
                                onClick={() => setFilter('Medical')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Medical'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Medical
                            </button>
                            <button
                                onClick={() => setFilter('Notary')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Notary'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Notary
                            </button>
                            <button
                                onClick={() => setFilter('Police')}
                                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    filter === 'Police'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                Police
                            </button>
                        </div>

                        {/* Export — respects the role chips, the search box and any
                            row selection, so what downloads is what's on screen. */}
                        <Button
                            onClick={handleExportCsv}
                            disabled={exportRows.length === 0}
                            title={
                                selectedIds.size > 0
                                    ? `Export ${exportRows.length} selected user(s)`
                                    : `Export all ${exportRows.length} user(s) matching the current filter`
                            }
                            className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                            Export CSV
                            {exportRows.length > 0 && (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                    {selectedIds.size > 0 ? `${exportRows.length} selected` : exportRows.length}
                                </span>
                            )}
                        </Button>

                        {/* Add Staff Button — creates agent / customer_success / medical / hr users */}
                        <Button
                            onClick={() => setIsInviteModalOpen(true)}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-2"
                        >
                            <UserPlus className="w-4 h-4" />
                            Add Staff
                        </Button>
                    </div>

                    {/* Table Container */}
                    <div className="border border-gray-100 rounded-2xl overflow-hidden flex-1 min-h-0">
                        <div className="h-full flex flex-col">
                            <DataTable
                                data={paginatedData}
                                columns={columns}
                                showCheckbox={true}
                                selectedRows={selectedRows}
                                onRowSelect={handleRowSelect}
                                onRowClick={(item) => {
                                    setSelectedUser(item);
                                    setShowUserModal(true);
                                }}
                                hoverable={true}
                                striped={true}
                            />
                        </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
                        {/* Rows per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Rows per page:</span>
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>

                        {/* Page numbers */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ‹
                            </button>

                            {currentPage > 2 && (
                                <>
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                                    >
                                        1
                                    </button>
                                    {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
                                </>
                            )}

                            {currentPage > 1 && (
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                                >
                                    {currentPage - 1}
                                </button>
                            )}

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
                                {currentPage}
                            </button>

                            {currentPage < totalPages && (
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                                >
                                    {currentPage + 1}
                                </button>
                            )}

                            {currentPage < totalPages - 1 && (
                                <>
                                    {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                                    <button
                                        onClick={() => setCurrentPage(totalPages)}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                                    >
                                        {totalPages}
                                    </button>
                                </>
                            )}

                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invite Agent Modal */}
            <CreateStaffModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />

            {/* User Details Modal */}
            <UserDetailsModal
                isOpen={showUserModal}
                onClose={() => {
                    setShowUserModal(false);
                    setSelectedUser(null);
                }}
                user={selectedUser}
            />
        </main>
    );
}
